import { useCallback, useRef, useState } from 'react';

import type { SlideContentV1 } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// Undo / redo history for slide content.
//
// Stores a snapshot stack of `SlideContentV1` payloads.  The host component
// decides *when* to commit a snapshot (e.g. on drag-end, on text-blur, on
// element create/delete) so that intermediate micro-changes (every pixel of
// a drag) don't flood the stack.
// ---------------------------------------------------------------------------

const MAX_HISTORY = 100;

export interface SlideHistorySnapshot {
  content: SlideContentV1;
  title: string;
}

function toSnapshot(content: SlideContentV1, title?: string): SlideHistorySnapshot {
  return {
    content,
    title: title ?? content.presentation.title,
  };
}

export function useSlideHistory(_initial: SlideContentV1 | null) {
  // Past snapshots (most recent at end). Does not include the current state.
  const pastRef = useRef<SlideHistorySnapshot[]>([]);
  // Future snapshots for redo (most recent undo at end).
  const futureRef = useRef<SlideHistorySnapshot[]>([]);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Synchronise boolean flags after any stack mutation.
  const sync = useCallback(() => {
    setCanUndo(pastRef.current.length > 0);
    setCanRedo(futureRef.current.length > 0);
  }, []);

  /** Push the current content as a snapshot before applying the next edit. */
  const pushSnapshot = useCallback(
    (current: SlideContentV1, title?: string) => {
      pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), toSnapshot(current, title)];
      futureRef.current = []; // new edit clears redo
      sync();
    },
    [sync]
  );

  /**
   * Undo: pop the last past snapshot and return it.
   * The caller must also supply the current content so it can be pushed onto
   * the redo stack.
   */
  const undo = useCallback(
    (current: SlideContentV1, title?: string): SlideHistorySnapshot | null => {
      if (pastRef.current.length === 0) return null;
      const snapshot = pastRef.current[pastRef.current.length - 1];
      pastRef.current = pastRef.current.slice(0, -1);
      futureRef.current = [...futureRef.current, toSnapshot(current, title)];
      sync();
      return snapshot;
    },
    [sync]
  );

  /** Redo: pop the last redo snapshot and return it. */
  const redo = useCallback(
    (current: SlideContentV1, title?: string): SlideHistorySnapshot | null => {
      if (futureRef.current.length === 0) return null;
      const snapshot = futureRef.current[futureRef.current.length - 1];
      futureRef.current = futureRef.current.slice(0, -1);
      pastRef.current = [...pastRef.current, toSnapshot(current, title)];
      sync();
      return snapshot;
    },
    [sync]
  );

  /** Reset history (e.g. on resource reload from server). */
  const resetHistory = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    sync();
  }, [sync]);

  return { pushSnapshot, undo, redo, canUndo, canRedo, resetHistory };
}
